import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import { REVIEW_API, type ReviewSession } from '@chess/api-review';
import { useSelect } from '@chess/api-review/react';
import { useConstraint, useConstraintRegistry } from '@chess/api-constraints/react';
import { TheoryList } from './TheoryList.tsx';
import { placeOf } from './place.ts';
import './theory.css';

// The container for the best-moves place above the stage tabs: exactly one
// of the theory list or the engine shortlist, decided in one render from the
// review session's `theory`, so the two never show together. While the
// board's Best moves visibility constraint hides them (a puzzle, from its
// start until "Back to the game") the place shows the owner's card instead
// (`review.shortlist.puzzle`, filled by review-puzzles), the third case of
// the same decision. The session
// owns the decision and the book: theory while the book has moves from the
// position, at the start (every first move), and while the game or its book
// is still loading; the engine shortlist (`review.shortlist.engine`)
// otherwise. The decision is `placeOf` (place.ts); this plugin only draws.

export function setup(app: PiletApi) {
  let session: ReviewSession | undefined;
  const ready = app.getApi<ReviewSession>(REVIEW_API).then((s) => (session = s));

  const Panel: React.FC = () => {
    const [review, setReview] = React.useState(session);
    React.useEffect(() => {
      if (!review) void ready.then(setReview);
    }, [review]);
    const theory = useSelect(review, (s) => s.theory);
    const registry = useConstraintRegistry(app);
    const scope = useSelect(review, (s) => s.scope);
    const visible = useConstraint(registry, scope, 'bestMovesVisibility').visible;
    const place = placeOf(visible, theory);
    if (place.kind === 'puzzle') return <app.Extension name="review.shortlist.puzzle" />;
    if (place.kind === 'engine') return <app.Extension name="review.shortlist.engine" />;
    return <TheoryList movePrefix={place.movePrefix} moves={place.moves} onPlay={(san) => review?.play(san as never)} />;
  };

  // The best-moves place is this container's; the engine shortlist, or the
  // owner's card while the best moves are hidden, renders inside it.
  app.registerExtension('review.shortlist', Panel);
}
